import { uuidToBase64 } from "../../../shared/utils";
import { WebRTCClient } from "./connectionToServer";
import { PEER_LEFT, SignallingChannel, SignalMessage } from "./peerSignaling";
import { getLocalSteamId64 } from "./steamNetworking";
import { getWebServicesList } from "./webServicesList";
import { Multiplayer } from "../../../shared/constants";

export class WebRTCConnectionPoller {
	private connection: WebRTCClient | null = null;
	private signallingChannel: SignallingChannel;
	private disposed = false;
	private pendingPeerConnection: RTCPeerConnection | null = null;
	private abortWaiters = new Set<(error: Error) => void>();
	private lastError: string | null = null;
	constructor() {
		this.signallingChannel = new SignallingChannel();
	}

	setError(error: string) {
		this.lastError = error;
	}

	clearError() {
		this.lastError = null;
	}

	getError() {
		return this.lastError;
	}

	/**
	 * Records a player readable reason and throws. Disposal is deliberately not reported this
	 * way: the player cancelled, so there is nothing to tell them.
	 */
	private fail(playerMessage: string, detail?: string): never {
		this.setError(playerMessage);
		throw new Error(detail ?? playerMessage);
	}

	dispose() {
		this.disposed = true;
		const waiters = [...this.abortWaiters];
		this.abortWaiters.clear();
		waiters.forEach(reject => reject(new Error("WebRTCConnectionPoller: disposed")));
		this.pendingPeerConnection?.close();
		this.pendingPeerConnection = null;
		if (this.connection) {
			this.connection.close();
			this.connection = null;
		}
		this.signallingChannel.close();
	}

	/** Rejects if the promise hasn't settled in time. For waits with no deadline of their own. */
	private withTimeout<T>(promise: Promise<T>, timeoutMs: number, what: string): Promise<T> {
		return new Promise<T>((resolve, reject) => {
			const timer = setTimeout(() => reject(new Error(`${what} timed out after ${timeoutMs}ms`)), timeoutMs);
			promise.then(
				value => { clearTimeout(timer); resolve(value); },
				error => { clearTimeout(timer); reject(error); }
			);
		});
	}

	private throwIfDisposed(context: string) {
		if (this.disposed) {
			throw new Error("WebRTCConnectionPoller: disposed " + context);
		}
	}

	/**
	 * Settles with the given promise, or rejects as soon as dispose() is called. Used for waits
	 * that have no timeout of their own - a data channel that never opens would otherwise hang
	 * the handshake forever with no way to cancel it.
	 */
	private abortable<T>(promise: Promise<T>, context: string): Promise<T> {
		if (this.disposed) {
			return Promise.reject(new Error("WebRTCConnectionPoller: disposed " + context));
		}
		return new Promise<T>((resolve, reject) => {
			const abort = (error: Error) => reject(error);
			this.abortWaiters.add(abort);
			promise.then(
				value => { this.abortWaiters.delete(abort); resolve(value); },
				error => { this.abortWaiters.delete(abort); reject(error); }
			);
		});
	}

	async openConnection(signallingServerLobbyCode: string, onLobbyOpen: (lobbyId: string) => void, timeoutMs: number) {
		this.clearError();
		try {
			const encodedSignallingServerLobbyCode = encodeURIComponent(uuidToBase64(signallingServerLobbyCode));
			const signallingEstablished = await this.signallingChannel.connect(encodedSignallingServerLobbyCode, Multiplayer.SignallingConnectTimeout);
			if (!signallingEstablished) {
				this.fail("Couldn't reach the connection service", "Failed to connect to signalling server for lobby " + encodedSignallingServerLobbyCode);
			}
			if (this.disposed) {
				console.error("WebRTCConnection: Disposed before connecting to signalling server for lobby " + encodedSignallingServerLobbyCode);
				this.signallingChannel.close();
				return;
			}
			console.log("Connected to signalling server for lobby " + encodedSignallingServerLobbyCode);
			onLobbyOpen(encodedSignallingServerLobbyCode);

			// Wait for peer to join and complete Steam/WebRTC handshake before starting WebRTC
			const localSteamId = await getLocalSteamId64();
			const deadline = Date.now() + timeoutMs;
			let handshakeResult;
			while (true) {
				try {
					handshakeResult = await this.signallingChannel.hostHandshake(localSteamId, Math.max(0, deadline - Date.now()));
					break;
				}
				catch (error) {
					if (this.disposed) throw error;
					const peerLeft = error instanceof Error && error.message === PEER_LEFT;
					if (!peerLeft || Date.now() >= deadline) {
						this.fail("Nobody joined in time", String(error));
					}
					//Someone reached the lobby and left again before pairing. On the signalling
					//server that also frees (or destroys) our slot, so re-open it and keep the
					//invite alive for the rest of our time rather than failing the whole attempt.
					console.warn("WebRTCConnection: peer left before pairing, re-opening the lobby");
					this.signallingChannel.close();
					const reopened = await this.signallingChannel.connect(encodedSignallingServerLobbyCode, Multiplayer.SignallingConnectTimeout);
					if (!reopened) {
						this.fail("Couldn't reach the connection service", "Failed to re-open lobby " + encodedSignallingServerLobbyCode);
					}
				}
			}
			if (handshakeResult.useSteam || this.disposed) {
				// Steam path not supported here - Steam hosting is handled separately via SteamConnectionToRemotePlayer
				this.signallingChannel.close();
				if (this.disposed) {
					throw new Error("Disposed during the signalling handshake");
				}
				this.fail("The other player connected over Steam instead", "Steam path not supported in WebRTC host connection");
			}

			if (this.signallingChannel.channel === null) {
				this.signallingChannel.close();
				this.fail("Lost contact with the connection service", "Signalling channel closed before starting WebRTC connection");
			}

			try {
				this.connection = await this.hostWebRtcConnection(this.signallingChannel.channel, encodedSignallingServerLobbyCode);
			}
			catch (error) {
				if (this.disposed) throw error;
				this.fail("Couldn't establish a direct connection", String(error));
			}

			this.signallingChannel.close(); //close the signalling channel as we don't need it anymore, it can be re-established if we need to reconnect

			//Hand ownership to the caller. Keeping it would mean a later dispose() closes the connection the game is using.
			const connection = this.connection;
			this.connection = null;
			return connection;
		}
		catch (error) {
			if (!this.disposed && !this.getError()) {
				this.setError("Couldn't set up the game"); //unexpected: nothing named a reason
			}
			console.error("WebRTCConnection: Failed to open connection", error);
			this.signallingChannel.close();
			return;
		}
	}

	async joinFromBrowser(lobbyCode: string, reconnectingClient?: WebRTCClient): Promise<WebRTCClient | null> {
		this.clearError();
		const signalingChannel = await this.signallingChannel.connect(lobbyCode, Multiplayer.SignallingConnectTimeout);
		if (!signalingChannel) {
			this.fail("Couldn't reach the connection service", "Failed to connect to lobby " + lobbyCode);
		}
		console.log("Joined lobby", lobbyCode);

		// Get our Steam ID (if available)
		const localSteamId = await getLocalSteamId64();

		// Exchange Steam IDs with host (timeout so reconnect retries can cycle)
		let handshakeResult;
		try {
			handshakeResult = await this.signallingChannel.clientHandshake(localSteamId, Multiplayer.WebrtcClientHandshakeTimeout);
		}
		catch (error) {
			if (this.disposed) throw error;
			this.fail("The host didn't respond", String(error));
		}

		if (handshakeResult.useSteam && handshakeResult.peerSteamId) {
			// Both have Steam - use Steam networking
			console.log("Using Steam networking to connect to host");
			this.signallingChannel.close();
			this.setError("The host is using Steam for this game");
			return null;
		} else {
			// Use WebRTC
			console.log("Using WebRTC connection");
			if (!this.signallingChannel.channel) {
				this.signallingChannel.close();
				if (!this.disposed) {
					this.setError("Lost contact with the connection service");
				}
				console.error("WebRTCConnection: Signalling channel closed before joining WebRTC connection");
				return null;
			}
			try {
				return await this.joinWebRtcConnection(this.signallingChannel.channel, lobbyCode, reconnectingClient);
			}
			catch (error) {
				if (this.disposed) throw error;
				this.fail("Couldn't connect to the host", String(error));
			}
		}
	}

	async hostWebRtcConnection(signalingChannel: WebSocket, lobbyId: string): Promise<WebRTCClient> {
		console.log("[HOST] Setting up WebRTC connection");

		const Services = await getWebServicesList();
		this.throwIfDisposed("while fetching ice servers");

		const pc = new RTCPeerConnection({ iceServers: Services.iceServers });
		this.pendingPeerConnection = pc;

		// Create data channel
		const dataChannel = pc.createDataChannel("game");

		let failReady: ((error: Error) => void) | null = null;
		const ready = new Promise<RTCDataChannel>((resolve, reject) => {
			failReady = reject;
			dataChannel.onopen = () => {
				console.log("[HOST] DataChannel open");
				resolve(dataChannel);
			};
			dataChannel.onerror = (e) => {
				console.warn("[HOST] DataChannel error", e);
				reject(e);
			};
			dataChannel.onclose = () => {
				console.log("[HOST] DataChannel closed");
			}
		});

		const sendSignal = (type: string, sdpOrCandidate: any) => {
			if (this.disposed || signalingChannel.readyState !== WebSocket.OPEN) return;
			signalingChannel.send(JSON.stringify({ action: "signal", type, sdpOrCandidate }));
		};

		// Handle ICE candidates
		pc.onicecandidate = (e) => {
			if (e.candidate) {
				console.log("[HOST] Sending ICE candidate");
				sendSignal("ice", e.candidate);
			}
		};

		pc.addEventListener('connectionstatechange', () => {
			console.log("[HOST] Connection state:", pc.connectionState);
			if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
				//ICE gave up: the data channel will never open, and nothing else would settle it
				failReady?.(new Error("[HOST] Peer connection " + pc.connectionState));
			}
		});

		// Handle signalling messages
		let iceCandidateQueue: RTCIceCandidateInit[] = [];

		const onSignallingMessage = async (event: MessageEvent) => {
			if (this.disposed) return;
			const msg: SignalMessage = JSON.parse(event.data);
			if (msg.event !== "signal") return;

			if (msg.type === "answer" && msg.sdpOrCandidate) {
				console.log("[HOST] Received answer");
				await pc.setRemoteDescription(msg.sdpOrCandidate);
				if (this.disposed) return;

				// Apply queued ICE candidates
				for (const candidate of iceCandidateQueue) {
					await pc.addIceCandidate(candidate);
				}
				iceCandidateQueue = [];
			} else if (msg.type === "ice" && msg.sdpOrCandidate) {
				if (pc.remoteDescription) {
					await pc.addIceCandidate(msg.sdpOrCandidate);
				} else {
					iceCandidateQueue.push(msg.sdpOrCandidate);
				}
			}
		};
		signalingChannel.addEventListener("message", onSignallingMessage);

		try {
			// Create and send offer
			const offer = await pc.createOffer({ offerToReceiveAudio: false, offerToReceiveVideo: false });
			this.throwIfDisposed("while creating the offer");
			await pc.setLocalDescription(offer);
			this.throwIfDisposed("while setting the local description");
			sendSignal("offer", pc.localDescription);
			console.log("[HOST] Sent WebRTC offer");

			const result = await this.abortable(
				this.withTimeout(ready, Multiplayer.DataChannelTimeout, "[HOST] data channel"),
				"while waiting for the data channel to open"
			);
			this.pendingPeerConnection = null;
			return new WebRTCClient(pc, result, lobbyId);
		}
		catch (error) {
			signalingChannel.removeEventListener("message", onSignallingMessage);
			ready.catch(() => { }); //it may still settle after we've stopped waiting on it
			pc.close();
			if (this.pendingPeerConnection === pc) {
				this.pendingPeerConnection = null;
			}
			throw error;
		}
	}

	async joinWebRtcConnection(signalingChannel: WebSocket, lobbyId: string, reconnectingClient?: WebRTCClient): Promise<WebRTCClient> {
		console.log("[CLIENT] Waiting for WebRTC connection");

		const Services = await getWebServicesList();
		this.throwIfDisposed("while fetching ice servers");

		const pc = new RTCPeerConnection({ iceServers: Services.iceServers });
		this.pendingPeerConnection = pc;

		let failReady: ((error: Error) => void) | null = null;
		const ready = new Promise<RTCDataChannel>((resolve, reject) => {
			failReady = reject;
			pc.ondatachannel = (event) => {
				const dataChannel = event.channel;
				dataChannel.onopen = () => {
					console.log("[CLIENT] DataChannel open");
					resolve(dataChannel);
				};
				dataChannel.onerror = (e) => {
					console.warn("[CLIENT] DataChannel error", e);
					reject(e);
				};
			};
		});

		//Sending on a socket the dispose already closed throws, so check both
		const sendSignal = (type: string, sdpOrCandidate: any) => {
			if (this.disposed || signalingChannel.readyState !== WebSocket.OPEN) return;
			signalingChannel.send(JSON.stringify({ action: "signal", type, sdpOrCandidate }));
		};

		// Handle ICE candidates
		pc.onicecandidate = (e) => {
			if (e.candidate) {
				console.log("[CLIENT] Sending ICE candidate");
				sendSignal("ice", e.candidate);
			}
		};

		pc.onconnectionstatechange = () => {
			console.log("[CLIENT] Connection state:", pc.connectionState);
			if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
				//ICE gave up: the data channel will never open, and nothing else would settle it
				failReady?.(new Error("[CLIENT] Peer connection " + pc.connectionState));
			}
		};

		// Handle signalling messages
		let iceCandidateQueue: RTCIceCandidateInit[] = [];

		//Named so it can be detached on abort - otherwise it keeps answering offers on a closed
		//peer connection
		const onSignallingMessage = async (event: MessageEvent) => {
			if (this.disposed) return;
			const msg: SignalMessage = JSON.parse(event.data);
			if (msg.event !== "signal") return;

			if (msg.type === "offer" && msg.sdpOrCandidate) {
				console.log("[CLIENT] Received offer");
				await pc.setRemoteDescription(msg.sdpOrCandidate);
				if (this.disposed) return;

				// Apply queued ICE candidates
				for (const candidate of iceCandidateQueue) {
					await pc.addIceCandidate(candidate);
				}
				iceCandidateQueue = [];

				// Create and send answer
				const answer = await pc.createAnswer();
				if (this.disposed) return;
				await pc.setLocalDescription(answer);
				sendSignal("answer", pc.localDescription);
				console.log("[CLIENT] Sent answer");
			} else if (msg.type === "ice" && msg.sdpOrCandidate) {
				if (pc.remoteDescription) {
					await pc.addIceCandidate(msg.sdpOrCandidate);
				} else {
					iceCandidateQueue.push(msg.sdpOrCandidate);
				}
			}
		};
		signalingChannel.addEventListener("message", onSignallingMessage);

		try {
			const result = await this.abortable(
				this.withTimeout(ready, Multiplayer.DataChannelTimeout, "[CLIENT] data channel"),
				"while waiting for the data channel to open"
			);
			this.pendingPeerConnection = null;
			signalingChannel.close();
			if (reconnectingClient) {
				const newConnection = new WebRTCClient(pc, result, lobbyId);
				reconnectingClient.copyConnection(newConnection);
				return reconnectingClient;
			}
			return new WebRTCClient(pc, result, lobbyId);
		}
		catch (error) {
			signalingChannel.removeEventListener("message", onSignallingMessage);
			ready.catch(() => { }); //it may still settle after we've stopped waiting on it
			pc.close(); //never handed to a WebRTCClient, so nothing else will close it
			if (this.pendingPeerConnection === pc) {
				this.pendingPeerConnection = null;
			}
			throw error;
		}
	}
}